import React, { useEffect, useRef, useState } from 'react'
import Map, {
  FullscreenControl,
  GeolocateControl,
  NavigationControl,
  Popup,
  ScaleControl,
} from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import useTrans from '@hooks/useTrans'
import useMapRef from '@hooks/useMapRef'
import { RivenditoreType } from '@utils/types'
import { scrollTo } from '@utils/safe'
import {
  checkValidCoords,
  clusterCountLayer,
  clusterLayer,
  getDistance,
  loadIcon,
  unclusteredPointGetIconLayer,
} from '@utils/rivenditori'
import { Target } from '@components/icons'
import { Button, Chip, FormGeocoder, WContainer } from '@components/atoms'
import { RivenditoreAccordionCard, RivenditoreMapModal } from '@components/molecules'
import styles from './RivenditoriMap.module.sass'

interface Props {
  rivenditori?: any[]
  className?: string
  rivenditoriFiltered?: any[]
}

const RivenditoriMap = (props: Props) => {
  const { rivenditori = [], className = '', rivenditoriFiltered = [] } = props

  const t = useTrans()
  const mapContainerRef = useRef(null)
  const [mapCurrent, mapRef] = useMapRef()

  const [selectedRivenditore, setSelectedRivenditore] = useState<
    (GeolocationPosition & RivenditoreType) | null
  >(null)

  const views = {
    map: t('Vedi Mappa'),
    list: t('Vedi Lista'),
  }

  const [geocoderResult, setGeocoderResult] = useState(undefined)
  const [viewMode, setViewMode] = useState(Object.keys(views)?.[0])
  const [currentLocation, setCurrentLocation] = useState<GeolocationPosition | null>(null)

  const [list, setList] = useState([])
  const [geoJSON, setGeoJSON] = useState<(GeolocationPosition & any) | null>(null)
  // sortCenter: punto da cui calcolare l'ordine dei risultati in lista, può essere determinato sia da ricercaFilter che da currentLocation
  const [sortCenter, setSortCenter] = useState(null)

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setCurrentLocation(position)
      })
    }
  }

  const handleClick = (e) => {
    const features = e.features[0]
    if (features) {
      const clusterName = features.layer?.id
      const clusterId = features.properties?.cluster_id
      if (clusterName === 'clusters' || clusterName === 'cluster-count') {
        mapCurrent.getSource('rivenditori').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || !zoom) return
          mapCurrent?.easeTo({ center: features.geometry.coordinates, zoom })
        })
      } else if (clusterName === 'unclustered-point') {
        mapCurrent?.easeTo({ center: features.geometry.coordinates })
        const sel = features.properties?.rivenditoreJSON
          ? JSON.parse(features.properties.rivenditoreJSON)
          : null
        // al click sul pin apro sia il popup nella desktop che nella mobile
        setSelectedRivenditore(sel)
      }
    } else {
      setSelectedRivenditore(null)
    }
  }

  const loadIcons = () =>
    new Promise<void>((resolve, reject) => {
      loadIcon(mapCurrent, resolve, reject, 'pin', '/images/pin.png')
      loadIcon(mapCurrent, resolve, reject, 'pinSelected', '/images/pinSelected.png')
      loadIcon(mapCurrent, resolve, reject, 'markerCluster', '/images/markerCluster.png')
      resolve()
    })

  const loadSource = () =>
    new Promise<void>((resolve, reject) => {
      if (!mapCurrent.getMap().getSource('rivenditori')) {
        mapCurrent.getMap().addSource('rivenditori', {
          type: 'geojson',
          data: geoJSON,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 60,
        })
      } else mapCurrent.getMap().getSource('rivenditori').setData(geoJSON)
      resolve()
    })

  const loadMapResources = async () => {
    await loadIcons()
    await loadSource()

    if (!mapCurrent.getMap().getLayer(clusterLayer.id)) mapCurrent.getMap().addLayer(clusterLayer)
    if (!mapCurrent.getMap().getLayer(clusterCountLayer.id))
      mapCurrent.getMap().addLayer(clusterCountLayer)
    if (!mapCurrent.getMap().getLayer(unclusteredPointGetIconLayer.id))
      mapCurrent.getMap().addLayer(unclusteredPointGetIconLayer)
  }

  const handleList = () => {
    if (sortCenter) {
      // mostro la lista solo quando c'è una ricerca con il punto centrale da cui calcolare l'ordine
      const map = mapCurrent?.getMap()

      const rivenditoriInView = rivenditori
        .filter((x) => checkValidCoords(parseFloat(x.latitudine), parseFloat(x.longitudine)))
        .filter((x) =>
          map.getBounds().contains([parseFloat(x.longitudine), parseFloat(x.latitudine)])
        )

      setList(
        rivenditoriInView.sort((a, b) =>
          getDistance(
            sortCenter.lng,
            sortCenter.lat,
            parseFloat(a.latitudine),
            parseFloat(a.longitudine)
          ) <
          getDistance(
            sortCenter.lng,
            sortCenter.lat,
            parseFloat(b.latitudine),
            parseFloat(b.longitudine)
          )
            ? -1
            : 1
        )
      )
    } else setList([])
  }

  const handleMapZoom = () => {
    handleList()
  }

  const handleMapMove = () => {
    handleList()
  }

  const centerMap = (lat, lng) => {
    if (checkValidCoords(lat, lng))
      mapCurrent?.easeTo({
        center: [lng, lat],
        zoom: 10,
      })
  }

  useEffect(() => {
    const handleResize = () => {
      mapCurrent?.resize()
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (currentLocation?.coords) {
      centerMap(currentLocation.coords.latitude, currentLocation.coords.longitude)
    }
    setSortCenter(currentLocation || null)
  }, [currentLocation])

  useEffect(() => {
    if (geocoderResult?.center) {
      centerMap(geocoderResult.center[1], geocoderResult.center[0])
    }
    setSortCenter(
      geocoderResult?.center
        ? { lat: geocoderResult.center[0], lng: geocoderResult.center[1] }
        : null
    )
  }, [geocoderResult])

  useEffect(() => {
    // Se c'è il punto da cui calcolare l'ordinamento della lista setto la lista
    // Metto un timeout perchè devo dar tempo alla mappa di spostarsi
    setTimeout(() => {
      handleList()
    }, 500)
  }, [sortCenter])

  useEffect(() => {
    if (mapCurrent) {
      const rivenditoriValidi = rivenditoriFiltered.filter((x) =>
        checkValidCoords(x.latitudine, x.longitudine)
      )
      if (rivenditoriValidi.length > 0) {
        const minLng = Math.min(...rivenditoriValidi.map((x) => parseFloat(x.longitudine)))
        const minLat = Math.min(...rivenditoriValidi.map((x) => parseFloat(x.latitudine)))
        const maxLng = Math.max(...rivenditoriValidi.map((x) => parseFloat(x.longitudine)))
        const maxLat = Math.max(...rivenditoriValidi.map((x) => parseFloat(x.latitudine)))

        const bbox = [
          [minLng, minLat],
          [maxLng, maxLat],
        ]

        mapCurrent.fitBounds(bbox, {
          padding: 100,
          maxZoom: 15,
        })
      }
    }
  }, [rivenditori, mapCurrent])

  useEffect(() => {
    setGeoJSON({
      type: 'FeatureCollection',
      features: rivenditori
        .filter((x) => checkValidCoords(x.latitudine, x.longitudine))
        .map((r) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [r.longitudine, r.latitudine],
          },
          properties: {
            icon: r.pk && r.pk === selectedRivenditore?.pk ? 'pinSelected' : 'pin',
            rivenditoreJSON: JSON.stringify({ ...r }),
          },
        })),
    })
  }, [rivenditori, selectedRivenditore])

  useEffect(() => {
    if (mapCurrent && mapCurrent.getMap().getSource('rivenditori')) loadSource()
  }, [geoJSON, mapCurrent])

  useEffect(() => {
    const map = mapCurrent?.getMap()
    if (map) {
      map.on('load', () => {
        map?.getStyle()?.layers.forEach(function (thisLayer) {
          if (thisLayer.id.indexOf('-label') > 0) {
            map?.setLayoutProperty(thisLayer.id, 'text-field', ['get', 'name_it'])
          }
        })
      })
    }
  }, [mapCurrent])

  // list e viewMode aprono/chiudono la lista, quando questo succede la mappa deve riadattarsi
  useEffect(() => {
    const map = mapCurrent?.getMap()
    map?.resize()
  }, [list])

  // La mappa si ridatta solo se sono in versione desktop o rientro nella visualizzazione mappa - altrimenti svuota la lista
  useEffect(() => {
    if (viewMode === 'map' || window.innerWidth >= 768) {
      const map = mapCurrent?.getMap()
      map?.resize()
    }
  }, [viewMode])

  return (
    <div
      className={`${styles.root} 
      ${viewMode ? styles[`root--${viewMode}View`] : ''} 
      ${sortCenter ? styles['root--listVisible'] : ''}
      ${className}`}
    >
      <WContainer>
        <div className={styles.form}>
          <FormGeocoder setResult={setGeocoderResult} className={styles.geocoder} />
          <div className={styles.locationOr}>{t('oppure')}</div>
          <Button
            label={t('Usa la tua posizione')}
            icon={<Target />}
            iconPos={'left'}
            variant={'secondary'}
            onClick={() => getCurrentLocation()}
            className={styles.positionButton}
          />
        </div>
        <div className={styles.toggler}>
          {Object.entries(views).map(([k, v]) => (
            <Chip key={k} label={v} selected={viewMode === k} onClick={() => setViewMode(k)} />
          ))}
        </div>
      </WContainer>
      <div className={styles.mapWrap} ref={mapContainerRef}>
        <div className={styles.list}>
          <div className={styles.listContent}>
            <h2 className={styles.listTitle}>{t('Risultati:')}</h2>
            <div className={styles.listList}>
              {!list?.length ? (
                <div className={styles.listEmpty}>{t('Nessun rivenditore nella zona')}</div>
              ) : (
                list?.map((x) => (
                  <RivenditoreAccordionCard
                    key={x.pk}
                    id={`rivenditorimap_list_${x.pk}`}
                    rivenditore={x}
                    open={x.pk === selectedRivenditore?.pk}
                    setOpen={(v) => {
                      setSelectedRivenditore(v ? x : null)
                      // Centro la mappa sul pin - solo nella desktop (in mobile solo quando torno sulla visualizzazione mappa altrimenti si svuota la lista)
                      if (v && window.innerWidth >= 768) centerMap(x.latitudine, x.longitudine)
                    }}
                  />
                ))
              )}
            </div>
          </div>
        </div>
        <div className={styles.map}>
          <Map
            ref={mapRef}
            mapboxAccessToken={process.env.MAPBOX_ACCESS_TOKEN}
            scrollZoom={false}
            initialViewState={{
              longitude: 12.674297,
              latitude: 42.6384261,
              zoom: 5,
            }}
            mapStyle="mapbox://styles/mapbox/streets-v11"
            interactiveLayerIds={['clusters', 'cluster-count', 'unclustered-point']}
            onClick={(e) => handleClick(e)}
            onLoad={() => loadMapResources()}
            onMoveEnd={() => handleMapMove()}
            onZoomEnd={() => handleMapZoom()}
          >      
            <GeolocateControl position="bottom-right" />
            <FullscreenControl position="bottom-right" />
            <NavigationControl position="bottom-right" />
            <ScaleControl />         
            {selectedRivenditore && (
              <Popup
                key={selectedRivenditore.pk || Math.random()}
                anchor="bottom"
                longitude={Number(selectedRivenditore.longitudine)}
                latitude={Number(selectedRivenditore.latitudine)}
                onClose={() => {
                  setSelectedRivenditore(null)
                }}
                className={styles.popup}
              >
                <RivenditoreMapModal
                  rivenditore={selectedRivenditore}
                  open={!!selectedRivenditore}
                  className={styles.mapModal}
                  onClose={() => {
                    setSelectedRivenditore(null)
                  }}
                />
              </Popup>
            )}
          </Map>
        </div>
      </div>
    </div>
  )
}

export default RivenditoriMap
